import { GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery } from '@/__generated__/graphql';
import {
  bankAccountsRowNames,
  flattenSensitiveBankAccountDataObject,
  maskedBankAccountFields
} from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/rowComponents/bankAccountsComponent';
import { TwoColumnWrapper } from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/styles';
import { IRenderTemplateRow } from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/types';
import { useResponsive } from '@/hooks/use-responsive';
import { themeVar } from '@/styles/theme';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { GeneralInformationItem } from './pageRowsPreview';

interface BankAccountsPreviewProps {
  row: IRenderTemplateRow;
  bankAccounts: GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery['legalEntity']['bankAccounts'];
  formData: any;
}

export const BankAccountsPreview = ({ row, bankAccounts, formData }: BankAccountsPreviewProps) => {
  const { isMobile } = useResponsive();

  const [selectedBankAccounts, setSelectedBankAccounts] = useState<
    (Omit<GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery['legalEntity']['bankAccounts'][number], 'data'> & {
      data: Record<string, string>;
    })[]
  >([]);

  useEffect(() => {
    const selectedBankAccounts = bankAccounts
      ?.filter((bankAccount) => formData?.[row.name]?.includes(bankAccount.id))
      ?.map((bankAccount) => {
        return { ...bankAccount, data: flattenSensitiveBankAccountDataObject(bankAccount.data) };
      });
    setSelectedBankAccounts(selectedBankAccounts ?? []);
  }, [row, bankAccounts, formData]);

  return (
    formData?.[row.name] && (
      <BankAccountsWrapper isMobile={isMobile}>
        {selectedBankAccounts.map((item) => (
          <BankAccountItem key={item.id}>
            {item.name && (
              <TwoColumnWrapper isMobile={isMobile}>
                <GeneralInformationItem>Beneficiary Bank Name</GeneralInformationItem>
                <GeneralInformationItem isBold>{item.name}</GeneralInformationItem>
              </TwoColumnWrapper>
            )}
            {Object.entries(item.data).map(
              ([key, value]) =>
                !!value &&
                key !== '__typename' && (
                  <TwoColumnWrapper key={key} isMobile={isMobile}>
                    <GeneralInformationItem>
                      {bankAccountsRowNames?.[key as keyof typeof bankAccountsRowNames] ?? _.startCase(key)}
                    </GeneralInformationItem>
                    <GeneralInformationItem isBold>
                      {maskedBankAccountFields.includes(key) ? (value as string).replace(/./g, '*') : (value as string)}
                    </GeneralInformationItem>
                  </TwoColumnWrapper>
                )
            )}
          </BankAccountItem>
        ))}
      </BankAccountsWrapper>
    )
  );
};

const BankAccountsWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: ${({ isMobile }) => (isMobile ? '100%' : '60%')};
`;

const BankAccountItem = styled.div`
  padding: 20px;
`;

const ApprovedIconWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
  color: ${themeVar('font.base')};
  padding: 10px 10px 0 10px;
`;
